import { getConfig } from "@/_Configuration/configuration";
import { buildEavIndices } from "@/components/PropertyWindow/eavIndexBuilder";
import {
    AttributeRow,
    ClassificationRow,
    EavRow,
    EntityClassificationRow,
    EntityIdRow,
    EntityMaterialRow,
    MaterialRow,
    PropertyViewModel,
    ValueRow,
} from "@/Models/Eav";
import Model from "@/Models/Model";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

const useEavModels = (models: Model[]): PropertyViewModel[] => {
    const [objects, setObjects] = useState<PropertyViewModel[]>([]);
    const eavCacheRef = useRef<Map<string, PropertyViewModel>>(new Map());

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            const cdn = getConfig().cdn;
            const cache = eavCacheRef.current;
            const result: PropertyViewModel[] = [];

            for (const src of models) {
                if (!src) continue;

                const modelId = src.modelId;
                const cached = cache.get(modelId);
                if (cached) {
                    result.push(cached);
                    continue;
                }

                const base = `${src?.url ?? cdn}/${src.folder}`;
                const paths = {
                    eav: src?.eav?.entity_attribute_values ?? `${base}/objects_eav.json`,
                    ids: src?.eav?.entities ?? `${base}/objects_id.json`,
                    vals: src?.eav?.values ?? `${base}/objects_val.json`,
                    attrs: src?.eav?.attributes ?? `${base}/objects_attr.json`,
                    classifications: src?.eav?.classifications ?? `${base}/classifications.json`,
                    materials: src?.eav?.materials ?? `${base}/materials.json`,
                    objClass: src?.eav?.entity_classifications ?? `${base}/objects_classification.json`,
                    objMat: src?.eav?.entity_materials ?? `${base}/objects_material.json`,
                };

                try {
                    const [eavRes, idsRes, valsRes, attrsRes, classRes, matRes, objClassRes, objMatRes] =
                        await Promise.all([
                            axios.get(paths.eav),
                            axios.get(paths.ids),
                            axios.get(paths.vals),
                            axios.get(paths.attrs),
                            axios.get(paths.classifications),
                            axios.get(paths.materials),
                            axios.get(paths.objClass),
                            axios.get(paths.objMat),
                        ]);

                    const ids = idsRes.data as EntityIdRow[];
                    const eav = eavRes.data as EavRow[];
                    const vals = valsRes.data as ValueRow[];
                    const attrs = attrsRes.data as AttributeRow[];
                    const classifications = classRes.data as ClassificationRow[];
                    const materials = matRes.data as MaterialRow[];
                    const objClass = objClassRes.data as EntityClassificationRow[];
                    const objMat = objMatRes.data as EntityMaterialRow[];

                    const indices = buildEavIndices(
                        ids,
                        eav,
                        attrs,
                        vals,
                        classifications,
                        objClass,
                        materials,
                        objMat
                    );

                    const model: PropertyViewModel = {
                        modelId,
                        ids: Object.fromEntries(ids.map((r) => [r.ExternalId, r.Id])),
                        indices,
                    };

                    cache.set(modelId, model);
                    result.push(model);
                } catch (err) {
                    console.error("EAV load failed for", modelId, err);
                }
            }

            if (!cancelled) {
                setObjects(result);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [models]);

    return objects;
};

export default useEavModels;
